"use client";
import { IUserFull } from "@gettactic/api";
import { useUserListByIds, useUserListPaginated } from "@gettactic/hooks";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { UnifiedSelect, SelectItem } from "@cmp-src/form/UnifiedSelect";
import { useDebouncedValue } from "@/lib/hooks/useDebouncedValue";
import { SearchIcon } from "@cmp-src/icons/icons";
import { cn } from "@gettactic/helpers";

type Props = {
  onChange: (ids: string[]) => void;
  /** Optional callback that provides full user data along with selection */
  onChangeWithData?: (users: IUserFull[]) => void;
  values?: string[];
  className?: string;
  disabled?: boolean;
  /** Visual variant - 'default' has border, 'ghost' has no border */
  variant?: "default" | "ghost";
  /** Custom render function for items - receives full user data */
  renderItem?: (user: IUserFull, isSelected: boolean) => React.ReactNode;
};

export const SelectUsers = ({
  className,
  onChange,
  onChangeWithData,
  values = [],
  disabled = false,
  variant = "ghost",
  renderItem
}: Props) => {
  const [missingUsers, setMissingUsers] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(query, 300);

  const userList = useUserListPaginated(debouncedQuery, 10);
  const users = userList.allPages;
  const userIncludeList = useUserListByIds(missingUsers);

  // Cache all users loaded across searches so selections from prior
  // search results are still available when Apply is clicked
  const userCacheRef = useRef<Map<string, IUserFull>>(new Map());

  useEffect(() => {
    if (userList.isFetching) {
      return;
    }
    const missing = values.filter((x) => !users.some((y) => y.id === x));
    setMissingUsers(missing);
  }, [values, userList.isFetching, users]);

  // Combine and deduplicate users
  const allUsers = useMemo(() => {
    const includeList = (userIncludeList?.data ?? []).filter(
      (x) => !!x
    ) as IUserFull[];

    const seen = new Set<string>();
    return users.concat(includeList).filter((user) => {
      if (seen.has(user.id)) return false;
      seen.add(user.id);
      return true;
    });
  }, [users, userIncludeList.data]);

  // Update the user cache whenever allUsers changes
  useEffect(() => {
    for (const user of allUsers) {
      userCacheRef.current.set(user.id, user);
    }
  }, [allUsers]);

  // Create a map for quick user lookup
  const usersMap = useMemo(() => {
    return new Map(allUsers.map((user) => [user.id, user]));
  }, [allUsers]);

  // Convert users to SelectItem format, ensuring selected values are always included
  const items: SelectItem[] = useMemo(() => {
    const userItems = allUsers.map((user) => ({
      id: user.id,
      name: user.name,
      keywords: [user.email, user.name]
    }));

    // Add placeholder items for any selected values not in the list
    const existingIds = new Set(userItems.map((item) => item.id));
    const missingSelectedItems = values
      .filter((id) => !existingIds.has(id))
      .map((id) => ({
        id,
        name: "Loading...",
        keywords: [] as string[]
      }));

    return [...userItems, ...missingSelectedItems];
  }, [allUsers, values]);

  // Convert selected IDs to SelectItem array
  const selectedValues: SelectItem[] = useMemo(() => {
    return values
      .map((id) => items.find((item) => item.id === id))
      .filter((item): item is SelectItem => !!item);
  }, [values, items]);

  const handleSelectMultiple = (selected: SelectItem[]) => {
    const selectedIds = selected.map((item) => item.id);
    onChange(selectedIds);

    // If onChangeWithData is provided, pass full user objects
    // Use the cache ref so users from previous searches are still available
    if (onChangeWithData) {
      const selectedUsers = selectedIds
        .map((id) => userCacheRef.current.get(id))
        .filter((user): user is IUserFull => !!user);
      onChangeWithData(selectedUsers);
    }
  };

  const hasValue = selectedValues.length > 0;

  return (
    <div className={className}>
      <UnifiedSelect
        items={items}
        values={selectedValues}
        onSelectMultiple={handleSelectMultiple}
        multiselect
        searchable
        withApply
        placeholder="Employee"
        disabled={disabled}
        hasNextPage={userList.hasNextPage}
        fetchNextPage={userList.fetchNextPage}
        isFetchingNextPage={userList.isFetchingNextPage}
        onSearchChange={setQuery}
        splitView
        contentWidth="480px"
        renderItem={
          renderItem
            ? (item, isSelected) => {
                const user = usersMap.get(item.id);
                if (user) {
                  return renderItem(user, isSelected);
                }
                return item.name;
              }
            : undefined
        }
        trigger={
          variant === "ghost" ? (
            <span
              className={cn(
                "flex h-9 cursor-pointer items-center gap-1 truncate text-sm",
                "mx-0 my-0 h-auto min-h-0 text-nowrap px-0 py-0 font-bold text-neutral-1000"
              )}
            >
              <span>
                {hasValue ? `${selectedValues.length} selected` : "Employee"}
              </span>
              <SearchIcon className="h-4 w-4 text-neutral-1000" />
            </span>
          ) : undefined
        }
        renderValue={
          variant === "default"
            ? (selected) => {
                if (
                  !selected ||
                  (Array.isArray(selected) && selected.length === 0)
                ) {
                  return (
                    <span className="flex items-center text-neutral-600">
                      <SearchIcon className="h-5 w-5" />
                      <span className="pl-2">Employee</span>
                    </span>
                  );
                }
                if (Array.isArray(selected)) {
                  return selected.length === 1
                    ? selected[0].name
                    : `${selected.length} employees selected`;
                }
                return selected.name;
              }
            : undefined
        }
      />
    </div>
  );
};
